/**
 * Pixels + events — Website Build Brief v2 §7.
 *
 * Everything is injected after load rather than blocking in <head>, because the
 * same brief sets a Lighthouse 90+ mobile floor and three third-party scripts are
 * the main threat to it. Retargeting audiences still build from the first visitor;
 * they just do not delay first paint.
 *
 * Each pixel is skipped entirely when its ID is blank, so the page is clean until
 * the real IDs land in src/config.ts.
 */

import { config } from "./config.ts";

/** Brief §7: the four events to track. */
export type TrackEvent =
  "sweep_claim" | "founding_apply" | "webinar_click" | "report_click";

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  fbq?: ((...args: unknown[]) => void) & { queue?: unknown[] };
  lintrk?: (...args: unknown[]) => void;
}

const w = window as AnalyticsWindow;

function injectScript(src: string, attrs: Record<string, string> = {}): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
  document.head.appendChild(s);
}

function initGa4(id: string): void {
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag(...args: unknown[]) {
    w.dataLayer?.push(args);
  };
  w.gtag("js", new Date());
  // UTMs are on the URL; GA4 picks them up automatically from location.
  w.gtag("config", id);
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
}

function initMeta(id: string): void {
  /* eslint-disable */
  // Standard Meta Pixel bootstrap, reformatted. Queues calls until the SDK loads.
  const n: any = (w.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue!.push(args);
  });
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  /* eslint-enable */
  injectScript("https://connect.facebook.net/en_US/fbevents.js");
  w.fbq?.("init", id);
  w.fbq?.("track", "PageView");
}

function initLinkedIn(id: string): void {
  const dl = ((
    w as unknown as { _linkedin_data_partner_ids?: string[] }
  )._linkedin_data_partner_ids ??= []);
  dl.push(id);
  injectScript("https://snap.licdn.com/li/insight.min.js");
}

/** Install whichever pixels have IDs configured. Safe to call once, after load. */
export function initAnalytics(): void {
  const { ga4MeasurementId, metaPixelId, linkedInPartnerId } = config.analytics;
  if (ga4MeasurementId) initGa4(ga4MeasurementId);
  if (metaPixelId) initMeta(metaPixelId);
  if (linkedInPartnerId) initLinkedIn(linkedInPartnerId);
}

/**
 * Fire an event into whichever pixels are live. Never throws — analytics must
 * not be able to break a form submission.
 */
export function track(
  event: TrackEvent,
  params: Record<string, unknown> = {}
): void {
  try {
    w.gtag?.("event", event, params);
    w.fbq?.("trackCustom", event, params);
    w.lintrk?.("track", { conversion_id: event });
  } catch {
    /* analytics is never load-bearing */
  }
}

/**
 * Brief §7: "UTM passthrough into source_utm." Captured from the landing URL and
 * kept for the session, so a submit after in-page navigation still carries it.
 */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

const UTM_STORAGE_KEY = "mycgo_utm";

export function captureUtm(): void {
  const params = new URLSearchParams(window.location.search);
  const found = UTM_KEYS.filter(k => params.get(k))
    .map(k => `${k}=${params.get(k)}`)
    .join("&");
  if (!found) return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, found);
  } catch {
    /* private browsing — fall through to the live URL below */
  }
}

export function getUtm(): string {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return window.location.search.replace(/^\?/, "");
}

/** Wire the CTA/link clicks the brief names, via [data-track] attributes. */
export function bindTrackedLinks(): void {
  document.querySelectorAll<HTMLElement>("[data-track]").forEach(el => {
    el.addEventListener("click", () => {
      const name = el.dataset.track;
      if (name === "webinar_click" || name === "report_click") track(name);
      else track("sweep_claim", { placement: name });
    });
  });
}
